<template>
  <div>
    <div class="mt-3 flex items-center gap-2">
      <span class="mr-1 text-xs font-medium text-gray-60">{{ $t('conversations.filters.ai_search.example_label') }}</span>
      <ui-tag
        v-for="suggestion in suggestions"
        :key="suggestion"
        theme="zennovate"
        tag="button"
        @click="useSuggestion(suggestion)"
      >
        {{ suggestion }}
      </ui-tag>
      <button
        type="button"
        :aria-label="$t('conversations.filters.ai_search.refresh')"
        :title="$t('conversations.filters.ai_search.refresh')"
        class="ml-1 flex h-6 w-6 items-center justify-center rounded-full text-gray-50 transition-colors hover:bg-gray-10 hover:text-gray-70"
        @click="submit"
      >
        <refresh-cw-icon class="icon-s" />
      </button>
    </div>
    <div v-if="isLoading" class="mt-8 flex items-center justify-center gap-2 p-2">
      <ui-spinner inline no-spinner-background spinner-color="blue-40" />
      <span class="text-xs text-gray-70">{{ $t('conversations.filters.ai_search.loading') }}</span>
    </div>
    <ui-alert
      v-else-if="!explanationDismissed && assistantResponse"
      compressed
      full-width
      closable
      class="mt-8"
      @close="explanationDismissed = true"
    >
      {{ assistantResponse }}
    </ui-alert>
    <div
      v-else-if="isFetched && hasSubmitted && !lastResultCount"
      class="mt-8 flex items-center justify-center p-2 text-xs text-gray-70"
    >
      {{ $t('conversations.filters.ai_search.no_results') }}
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'
import { useQuery } from '@tanstack/vue-query'
import { UiSpinner, UiTag, UiAlert } from '@klausapp/ui-kit'
import { RefreshCwIcon } from '@klausapp/ui-kit/icons'
import { toast } from '@/components/toast'
import i18n from '@/i18n'
import { session } from '@/composables/useSession'

import {
  deconstructFilterOptions,
  type DeconstructedOption,
} from '../../utils/filters'
import { type DefaultFilterOption } from '../../api/filters'
import { getAiFilterOptions } from '../../api/ai-filters'

const props = defineProps<{
  options: DefaultFilterOption[]
  autoqaFields: DefaultFilterOption[]
  spotlightFields: DefaultFilterOption[]
  prompt: string
}>()

const emit = defineEmits<{
  (e: 'ai-results', value: DeconstructedOption[]): void
  (e: 'use-suggestion', value: string): void
}>()

const explanationDismissed = ref(false)
const hasSubmitted = ref(false)
const lastResultCount = ref(0)
const assistantResponse = ref('')
const submittedPrompt = ref('')

const suggestions = computed(() => [
  i18n.t('conversations.filters.ai_search.example_1'),
  i18n.t('conversations.filters.ai_search.example_2'),
  i18n.t('conversations.filters.ai_search.example_3'),
])

const {
  refetch,
  isFetching: isLoading,
  isFetched,
} = useQuery({
  queryKey: ['ai-filter-options', session.workspace.id, submittedPrompt],
  queryFn: () => getAiFilterOptions(submittedPrompt.value),
  enabled: false,
  refetchOnMount: false,
})

const useSuggestion = (suggestion: string) => {
  // reflect the clicked suggestion in the input, then run it
  emit('use-suggestion', suggestion)
  submitWithPrompt(suggestion)
}

const submit = () => submitWithPrompt(props.prompt)

async function submitWithPrompt(prompt: string) {
  if (!prompt) return
  submittedPrompt.value = prompt
  hasSubmitted.value = true
  explanationDismissed.value = false

  const { data: response, error } = await refetch()

  if (error || !response) {
    console.error('Error fetching AI filter options', error)
    toast({ status: 'error', message: i18n.t('conversations.filters.ai_search.error') })
    return
  }

  const { options, autoQaOptions } = response

  const results = deconstructFilterOptions({
    options: options || [],
    autoQaOptions: autoQaOptions || [],
    spotlightOptions: [],
    customCategoryOptions: [],
    intelligentFilterOptions: [],
    defaultOptions: props.options || [],
    defaultAutoQaOptions: props.autoqaFields || [],
    defaultSpotlightOptions: props.spotlightFields || [],
  })

  lastResultCount.value = results.length
  assistantResponse.value = response.assistantResponse || ''
  emit('ai-results', results)
}

defineExpose({ submit })
</script>
